import { type UseQueryOptions, useQuery } from '@tanstack/react-query';
import { getCollectionV1WorkspacesWorkspaceIdAssetsImagesCollectionsCollectionIdGet } from '../../generated';
import type { GetCollectionV1WorkspacesWorkspaceIdAssetsImagesCollectionsCollectionIdGetData } from '../../generated';
import type { GetCollectionV1WorkspacesWorkspaceIdAssetsImagesCollectionsCollectionIdGetError } from '../../generated';
import type { GetCollectionV1WorkspacesWorkspaceIdAssetsImagesCollectionsCollectionIdGetResponse } from '../../generated';
import type { CollectionLastScoringFields } from './collection-last-scoring';

export type GetCollectionQueryVariables =
  GetCollectionV1WorkspacesWorkspaceIdAssetsImagesCollectionsCollectionIdGetData['path'];

// `CollectionLastScoringFields` is a hand-written seam; see `./collection-last-scoring.ts`.
export type GetCollectionQuery = GetCollectionV1WorkspacesWorkspaceIdAssetsImagesCollectionsCollectionIdGetResponse &
  CollectionLastScoringFields;

export const useGetCollectionQuery = <
  TData = GetCollectionQuery,
  TError = GetCollectionV1WorkspacesWorkspaceIdAssetsImagesCollectionsCollectionIdGetError,
>(
  { workspace_id, collection_id }: GetCollectionQueryVariables,
  options?: Omit<UseQueryOptions<GetCollectionQuery, TError, TData>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery<GetCollectionQuery, TError, TData>({
    queryKey: ['GetCollection', { workspace_id, collection_id }],
    queryFn: async () => {
      const { data } = await getCollectionV1WorkspacesWorkspaceIdAssetsImagesCollectionsCollectionIdGet({
        path: { workspace_id, collection_id },
        throwOnError: true,
      });
      return data;
    },
    ...options,
  });
};

useGetCollectionQuery.getKey = ({ workspace_id, collection_id }: GetCollectionQueryVariables) => [
  'GetCollection',
  { workspace_id, collection_id },
];

useGetCollectionQuery.fetcher = (
  { workspace_id, collection_id }: GetCollectionQueryVariables,
  _headers?: RequestInit['headers'],
) => {
  return async () => {
    const { data } = await getCollectionV1WorkspacesWorkspaceIdAssetsImagesCollectionsCollectionIdGet({
      path: { workspace_id, collection_id },
      throwOnError: true,
    });
    return data;
  };
};
